import { BLOCKS } from '@contentful/rich-text-types';

const DIACRITICS = /[\u0300-\u036f]/g;

// Case- and accent-insensitive form used for matching ("Ação" -> "acao").
export const normalizeSearchText = (value = '') =>
  String(value).normalize('NFD').replace(DIACRITICS, '').toLowerCase();

export const nodeText = node => {
  if (!node) return '';
  if (node.nodeType === 'text') return node.value || '';
  return (node.content || []).map(nodeText).join(node.nodeType === BLOCKS.DOCUMENT ? ' ' : '');
};

// PT falls back to EN when a translation is missing.
export const localized = (en, pt, language) => (language === 'pt' ? pt || en : en);

// Ranges [start, end) in `text` that match `query`, ignoring case and accents.
export const findMatches = (text, query) => {
  const needle = normalizeSearchText(query).trim();
  if (!needle || !text) return [];

  let normalized = '';
  const origins = [];
  for (let i = 0; i < text.length; i += 1) {
    const piece = normalizeSearchText(text[i]);
    for (let j = 0; j < piece.length; j += 1) {
      normalized += piece[j];
      origins.push(i);
    }
  }

  const ranges = [];
  let from = normalized.indexOf(needle);
  while (from !== -1) {
    const last = from + needle.length - 1;
    ranges.push([origins[from], origins[last] + 1]);
    from = normalized.indexOf(needle, from + needle.length);
  }
  return ranges;
};

export const matchesQuery = (text, query) =>
  normalizeSearchText(text).includes(normalizeSearchText(query).trim());

// Keeps the questions whose title or answer match; a matching group title keeps all its questions.
export const searchFaqGroups = (groups, query, language) =>
  (groups || [])
    .map(group => {
      const faqs = (group.faqs || []).filter(Boolean);
      if (matchesQuery(localized(group.title, group.titlePt, language) || '', query)) {
        return { ...group, faqs };
      }
      return {
        ...group,
        faqs: faqs.filter(faq =>
          matchesQuery(
            `${localized(faq.title, faq.titlePt, language) || ''} ${nodeText(localized(faq.copy, faq.copyPt, language))}`,
            query,
          ),
        ),
      };
    })
    .filter(group => group.faqs.length > 0);

// `passkeys` (Face ID) is part of signInModes; the alias keeps groups tagged before the flags merged.
const FAQ_FLAG_ALIASES = Object.freeze({ passkeys: 'signInModes' });

// A group's `featureFlag` (Contentful) is the runtime /features flag that gates the feature it explains,
// so the FAQ ships and hides together with the feature. Empty = always shown; an unknown key is hidden.
// `!key` shows the group only while the flag is off (content the feature replaces).
export const filterFaqGroupsByFlags = (groups, features = {}) => {
  const flags = features || {};
  return (groups || []).filter(group => {
    const flag = typeof group?.featureFlag === 'string' ? group.featureFlag.trim() : '';
    if (!flag) return true;
    const negated = flag.startsWith('!');
    const name = negated ? flag.slice(1).trim() : flag;
    const key = FAQ_FLAG_ALIASES[name] || name;
    if (!Object.prototype.hasOwnProperty.call(flags, key)) {
      console.warn(`FAQ group "${group.id}" uses unknown feature flag "${flag}"; hiding it.`);
      return false;
    }
    return negated ? flags[key] !== true : flags[key] === true;
  });
};
